import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { CheckoutActions } from "../actions";
import { ApiService } from "../../../../SGRE-shared/services/api.service";
import { switchMap, map, catchError, of, mergeMap, takeUntil, concatMap } from "rxjs";
import { LoggerService, tryNormalizeHttpError } from "@spartacus/core";
import { pDropdown } from "../../../../SGRE-shared/models/primeng.model";
import { CheckoutCartDetails, DeliveryAddress } from "../../models/checkout.model";
import { AppConstants } from "../../../../SGRE-shared/constants/app-constant";
import { CheckoutDeliveryModesConnector } from "@spartacus/checkout/base/core";
import { GlobalService } from "../../../../SGRE-shared/services/global.service";
import { DeliveryMode } from "@spartacus/cart/base/root";
import { CheckoutCartsState } from "../checkout.state";
import { CheckoutCartsEffectsService } from "./checkout-carts-effects.service";
import { Addresses } from "../../../../SGRE-shared/models/legalEntityList.model";

@Injectable()
export class CheckoutShippingAddressEffects {

    protected logger = inject(LoggerService);

    constructor(
        private actions$: Actions,
        private apiService: ApiService,
        private checkoutDeliveryModesConnector: CheckoutDeliveryModesConnector,
        private globalService: GlobalService,
        private checkoutCartsEffectsService: CheckoutCartsEffectsService,
    ) { }

    loadShippingAddress$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CheckoutActions.loadShippingAddress),
            mergeMap(({ cartId, accordionType }) => {                       // Use mergeMap to handle simultaneous requests from multiple child components.
                let paramsObject = AppConstants.DEFAULT_PAYLOAD;
                return this.apiService.getShippingAddress(cartId, paramsObject)
                    .pipe(
                        takeUntil(this.actions$.pipe(ofType(CheckoutActions.clearCheckoutState))),
                        map((data: Addresses) => {
                            let addresses: pDropdown[] = [];
                            const otherAddress: pDropdown = {
                                label: DeliveryAddress.OTHER_ADDRESS_LABEL,
                                value: DeliveryAddress.OTHER_ADDRESS_VALUE,
                                id: DeliveryAddress.OTHER_ADDRESS_VALUE
                            };
                            if (data?.addresses) {
                                for (const item of data.addresses) {
                                    let obj: pDropdown = { label: item?.companyName, value: item, id: item.id }
                                    addresses = [...addresses, obj];
                                }
                            };
                            const payload = [...addresses, otherAddress];
                            return CheckoutActions.loadShippingAddressSuccess({ payload, accordionType });
                        }),
                        catchError(error => of(CheckoutActions.loadShippingAddressFail({
                            payload: tryNormalizeHttpError(error, this.logger),
                            accordionType
                        })))
                    );
            })
        )
    );

    updateExistingShippingAddress$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CheckoutActions.updateExistingShippingAddress),
            switchMap(({ cartId, addressId, accordionType, orderType, legalEntityId }) => {
                return this.apiService.putShippingAddress(cartId, addressId)
                    .pipe(
                        concatMap(() =>
                            this.checkoutDeliveryModesConnector.getSupportedModes(this.globalService.getUserId(), cartId)
                                .pipe(
                                    map((deliveryModesVal: DeliveryMode[]) => {
                                        return deliveryModesVal?.map(item => {
                                            const obj: pDropdown = { label: item?.name, value: item, id: item?.code };
                                            return obj;
                                        });
                                    })
                                )
                        ),
                        concatMap((deliveryModesData: pDropdown[]) => {
                            const paramsObject = {
                                fields: 'FULL',
                                legalEntityId
                            };
                            return this.apiService.getCartsList(cartId, paramsObject)
                                .pipe(
                                    map((checkoutCartsVal: CheckoutCartDetails) => {
                                        const checkoutCartsData: CheckoutCartsState = this.checkoutCartsEffectsService.modifyCartData(checkoutCartsVal, orderType);
                                        return CheckoutActions.updateShippingAddressSuccess({ deliveryModesData, checkoutCartsData, accordionType });
                                    })
                                );
                        }),
                        takeUntil(this.actions$.pipe(ofType(CheckoutActions.clearCheckoutState))),
                        catchError((error) =>
                            of(
                                CheckoutActions.updateShippingAddressFail({
                                    payload: tryNormalizeHttpError(error, this.logger),
                                    accordionType,
                                })
                            )
                        )
                    )
            })
        )
    );

    updateOtherShippingAddress$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CheckoutActions.updateOtherShippingAddress),
            switchMap(({ updateCartId, getCartId, payload, accordionType, orderType, legalEntityId }) => {
                return this.apiService.addOtherShippingAddress(updateCartId, payload)
                    .pipe(
                        takeUntil(this.actions$.pipe(ofType(CheckoutActions.clearCheckoutState))),
                        switchMap(() => {
                            let paramsObject = {
                                fields: 'FULL',
                                legalEntityId
                            }
                            return this.apiService.getCartsList(getCartId, paramsObject)
                                .pipe(
                                    takeUntil(this.actions$.pipe(ofType(CheckoutActions.clearCheckoutState))),
                                    map((checkoutCartsVal: CheckoutCartDetails) => {
                                        let checkoutCartsData: CheckoutCartsState = this.checkoutCartsEffectsService.modifyCartData(checkoutCartsVal, orderType);
                                        return CheckoutActions.updateOtherShippingAddressSuccess({ payload: checkoutCartsData, accordionType });
                                    })
                                );
                        }),
                        catchError(error => of(CheckoutActions.updateShippingAddressFail({
                            payload: tryNormalizeHttpError(error, this.logger),
                            accordionType
                        })))
                    );
            }),
        )
    );
}